import React from 'react';
import { useConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  Block,
  CertifiedDrawing,
  DRAWINGS_INTRO,
  DrawingListBlockData,
  RowFragment,
  blankCertifiedDrawing,
  designCriteriaSentence,
} from '../../../config';
import LiveText from '../../LiveText';
import './drawing-list-block.scss';

interface DrawingListBlockProps {
  block: Block;
  onChange: (data: DrawingListBlockData) => void;
  isEditing: boolean;
  /**
   * When the list spans more than one page, the drawings this page shows.
   * Absent means the whole list (the measurement copy, and any list that fits).
   */
  fragment?: RowFragment;
}

/**
 * The certified drawings of an RWD Design Certificate: the table of every
 * drawing the certificate covers.
 *
 * A certificate created before the lead-in became editable draws the
 * design-criteria sentence and "listed below:" here too, composed from the
 * rows (`showsLeadIn` absent). A new one carries both as ordinary paragraphs
 * above the block, so the engineer can reword them, and this draws rows only.
 *
 * The rows are a COPY of the stage's drawing set taken when the document was
 * created, not a live read (see DrawingListBlockData). A certificate is a
 * statement about drawings as they were on the day it was signed.
 *
 * As with the instruction list, empty rows are rendered rather than skipped and
 * DocumentPDF renders them too: the editor measures this block to decide where
 * the page breaks, and anything one surface shows and the other hides makes
 * that measurement a lie.
 */
const DrawingListBlock: React.FC<DrawingListBlockProps> = ({
  block,
  onChange,
  isEditing,
  fragment,
}) => {
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const data = block.data as DrawingListBlockData;
  const allRows = data.rows ?? [];

  // Only this page's slice is rendered, but every edit addresses the WHOLE
  // list -- otherwise removing a drawing on page 2 would remove the wrong one.
  const rows = fragment ? allRows.slice(fragment.rowStart, fragment.rowEnd) : allRows;

  // The two lead-in sentences are said once, so a continuation page shows the
  // rows alone. DocumentPDF does the same, and DocumentEditor measures the
  // lead-in separately for exactly this reason.
  const isContinuation = !!fragment && fragment.part > 0;
  // A certificate created since the lead-in became editable paragraphs draws
  // rows only; an older one still owns its two sentences (see showsLeadIn).
  const showsLeadIn = data.showsLeadIn !== false && !isContinuation;

  const setRows = (next: CertifiedDrawing[]) => onChange({ ...data, rows: next });

  const updateRow = (id: string, patch: Partial<CertifiedDrawing>) =>
    setRows(allRows.map(r => (r.id === id ? { ...r, ...patch } : r)));

  /**
   * A row the drawing set owns just goes -- Refresh brings it straight back.
   * One the author typed in themselves (no sourceId) has no counterpart in the
   * set, so nothing would restore it; that one is asked about, unless it is
   * still blank and holds nothing to lose.
   */
  const removeRow = async (row: CertifiedDrawing) => {
    const isAuthored = row.sourceId === null;
    const hasContent = !!(row.drawing_number || row.issue_number || row.title);
    if (isAuthored && hasContent) {
      const proceed = await confirm({
        title: 'Remove this drawing?',
        message: (
          <>
            <p>It was added here rather than taken from the stage&rsquo;s drawing set, so refreshing will not bring it back:</p>
            <ul>
              <li>{[row.drawing_number, row.issue_number && `Issue ${row.issue_number}`, row.title].filter(Boolean).join(' \u2014 ')}</li>
            </ul>
          </>
        ),
        confirmLabel: 'Remove',
        tone: 'danger',
      });
      if (!proceed) return;
    }
    setRows(allRows.filter(r => r.id !== row.id));
  };

  // Blank, and marked as the author's own so a refresh keeps it. Certificates
  // occasionally cite a drawing the stage's set does not hold.
  const addRowAfter = (id: string) => {
    const index = allRows.findIndex(r => r.id === id);
    const next = [...allRows];
    next.splice(index + 1, 0, blankCertifiedDrawing());
    setRows(next);
  };

  return (
    <div className="drawing-list-block">
      {confirmDialog}
      {showsLeadIn && (
        <>
          {/* Composed from the rows, never typed: it names the design standards
              and the site classification, both of which the refresh updates. */}
          <p className="drawing-list-block__criteria">{designCriteriaSentence(data)}</p>

          <p className="drawing-list-block__intro">{DRAWINGS_INTRO}</p>
        </>
      )}

      {allRows.length === 0 ? (
        // The same words DocumentPDF draws, in editing and out of it. What to
        // do about it belongs in the Fields sidebar beside the Refresh button:
        // a longer sentence here would be one the editor measures and the PDF
        // does not, which is how a block ends up a line taller on screen than
        // in the export.
        <p className="drawing-list-block__empty">No drawings listed.</p>
      ) : (
        <div className="drawing-list-block__rows">
          {rows.map(row => (
            <div key={row.id} className="drawing-row" data-drawing-row={row.id}>
              {/* The same bullet and indent as the standards list above, so the
                  two read as one document. contentEditable false so it cannot be
                  typed over or deleted. */}
              <span className="drawing-row__bullet" contentEditable={false}>•</span>
              <LiveText
                className="drawing-row__number"
                value={row.drawing_number}
                editable={isEditing}
                restoreKey={`${block.id}:${row.id}:number`}
                onCommit={drawing_number => updateRow(row.id, { drawing_number })}
              />
              <span className="drawing-row__issue">
                {/* The label is chrome and the number is the data, so they are
                    two elements: a label inside the editable text would be
                    committed as part of the value. */}
                {'Issue '}
                <LiveText
                  value={row.issue_number}
                  editable={isEditing}
                  restoreKey={`${block.id}:${row.id}:issue`}
                  onCommit={issue_number => updateRow(row.id, { issue_number })}
                />
              </span>
              <LiveText
                className="drawing-row__title"
                value={row.title}
                editable={isEditing}
                restoreKey={`${block.id}:${row.id}:title`}
                onCommit={title => updateRow(row.id, { title })}
              />

              {/* No design-standard control here: which drawings are standards
                  is set on the stage's drawing set in the RWD PEP, and Refresh
                  brings that across. One switch, in one place. */}
              {isEditing && (
                <div className="drawing-row__actions" contentEditable={false}>
                  <button
                    type="button"
                    title="Add a drawing below"
                    aria-label="Add a drawing below"
                    onClick={() => addRowAfter(row.id)}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                      <path d="M8 3v10M3 8h10" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    title="Remove this drawing"
                    aria-label={`Remove ${row.drawing_number || 'this drawing'}`}
                    onClick={() => { void removeRow(row); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DrawingListBlock;
